import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Dialog, Inline, Stack, t, TextField, Timeline, toast } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../lib/api';
import { relativeTime, shortDate } from '../lib/format';

type Installation = maintenance.components['schemas']['Installation'];
const UNDER_WAY = ['retiring', 'unlinking', 'purging', 'resetting'];
const RETIRE_STEPS = ['retire', 'unlink', 'purge', 'reset'];
/**
 * SPEC-034 § 12.4: the retirement's steps in their order, each with the installation step that records it done (the
 * supervision only when the environment had one; an internal application, D102, has none).
 */
const RETIRE_PROGRESS = [
  { id: 'retire', done: 'retire', running: 'retiring' },
  { id: 'unwatch', done: 'unwatch', running: 'unlinking' },
  { id: 'detach', done: 'detach', running: 'unlinking' },
  { id: 'unexpose', done: 'unexpose', running: 'unlinking' },
] as const;

/**
 * SPEC-034 § 12.4 (lot 1.5): « Retirer l'environnement » — the name typed and
 * a reason; in production another administrator approves. Then the steps run
 * (frozen, stopped with its volumes kept, out of the catalogue and of Caddy);
 * after the grace period its volumes go on a second confirmation, and an
 * emptied server can go back to the reserve.
 */
export function EnvironmentRetire({ env, canOperate, asking, onAskClose, onStepUp }: {
  env: Installation; canOperate: boolean; asking: boolean; onAskClose: () => void; onStepUp: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [purging, setPurging] = useState(false);
  const [reserve, setReserve] = useState(false);
  const [sending, setSending] = useState(false);
  const production = env.environmentType === 'production';
  const path = { params: { path: { id: env.id } } };
  const graceOver = !env.graceUntil || Date.parse(env.graceUntil) <= Date.now();

  const send = async (call: () => Promise<unknown>, done: string, close: () => void) => {
    setSending(true);
    try {
      await call();
      close();
      setName('');
      setReason('');
      toast({ tone: 'info', title: done });
      void queryClient.invalidateQueries({ queryKey: ['environment', env.deployment] });
    } catch (err) {
      if (err instanceof ApiFailure && err.body.code === 'MFA_STEP_UP_REQUIRED') { close(); onStepUp(); return; }
      toast({ tone: 'neg', title: err instanceof ApiFailure ? err.body.message : String(err), description: err instanceof ApiFailure ? err.body.hint : undefined });
    } finally {
      setSending(false);
    }
  };

  const retire = () => send(
    async () => unwrap(await api.maintenance.POST('/installations/{id}/retire', { ...path, body: { name, reason } })),
    t(production ? 'environment.retire.requested' : 'environment.retire.started', { deployment: env.deployment }), onAskClose,
  );
  const decide = (decision: 'approve' | 'cancel') => send(
    async () => unwrap(await api.maintenance.POST('/installations/{id}/retire/{decision}', { params: { path: { id: env.id, decision } } })),
    t(`environment.retire.${decision}d`, { deployment: env.deployment }), () => setDeciding(false),
  );
  const purge = () => send(
    async () => unwrap(await api.maintenance.POST('/installations/{id}/purge', { ...path, body: { name, reserve } })),
    t('environment.retire.purgeStarted', { deployment: env.deployment }), () => setPurging(false),
  );
  const resume = () => send(
    async () => unwrap(await api.maintenance.POST('/installations/{id}/{action}', { params: { path: { id: env.id, action: 'resume' } } })),
    t('environment.retire.resumed'), () => undefined,
  );

  const nameField = (
    <TextField id="retire-name" label={t('environment.retire.name', { deployment: env.deployment })} value={name} mono
      hint={t('environment.retire.nameHint')} onChange={e => setName(e.target.value)} />
  );

  return (
    <>
      {env.state === 'retire_requested' && (
        <Alert tone="warn" title={t('environment.retire.requestedTitle')}
          action={canOperate ? t('environment.retire.review') : undefined} onAction={() => setDeciding(true)}>
          {t('environment.retire.requestedBody', { reason: env.retireReason ?? '—' })}
        </Alert>
      )}
      {UNDER_WAY.includes(env.state) && (
        <Alert tone="info" title={t(`environment.retire.underWay.${env.state}`)}>{t('environment.retire.underWayBody')}</Alert>
      )}
      {/* The steps of the retirement, as the installation's (DSM Timeline), while it runs or once it stopped on one. */}
      {(['retiring', 'unlinking'].includes(env.state) || (env.state === 'failed' && ['retire', 'unlink'].includes(env.failedStep ?? ''))) && (() => {
        const steps = RETIRE_PROGRESS.filter(p => p.id !== 'unwatch' || env.steps.watch);
        const current = steps.find(p => !env.steps[p.done])?.id;
        return (
          <Timeline label={t('environment.retire.progressLabel')} items={steps.map(p => ({
            id: p.id,
            title: t(`environment.retire.progress.${p.id}`),
            status: (env.steps[p.done] ? 'done' : p.id !== current ? 'upcoming' : env.state === 'failed' ? 'error' : 'current') as 'done' | 'upcoming' | 'error' | 'current',
            when: env.steps[p.done] ? relativeTime(env.steps[p.done]!) : undefined,
            description: p.id === current && env.state === 'failed' ? env.failure ?? undefined : t(`environment.retire.progress.${p.id}Help`),
          }))} />
        );
      })()}
      {env.state === 'retired' && (
        <Alert tone="warn" title={t('environment.retire.retiredTitle')}
          action={canOperate && graceOver ? t('environment.retire.purgeAction') : undefined} onAction={() => setPurging(true)}>
          {graceOver ? t('environment.retire.graceOver') : t('environment.retire.graceBody', { until: shortDate(env.graceUntil) })}
        </Alert>
      )}
      {env.state === 'purged' && <Alert tone="pos" title={t('environment.retire.purgedTitle')}>{t(env.steps.reset ? 'environment.retire.purgedReserve' : 'environment.retire.purgedBody', { server: env.server })}</Alert>}
      {env.state === 'failed' && RETIRE_STEPS.includes(env.failedStep ?? '') && (
        <Alert tone="neg" title={t('environment.retire.failedTitle', { step: t(`environment.retire.steps.${env.failedStep}`) })}
          action={canOperate ? t('environment.retire.resume') : undefined} onAction={() => void resume()}>
          {env.failure ?? ''}
        </Alert>
      )}

      <Dialog
        open={asking}
        onClose={onAskClose}
        tone="danger"
        title={t('environment.retire.title', { deployment: env.deployment })}
        description={t(production ? 'environment.retire.bodyProduction' : 'environment.retire.body', { domain: env.domain })}
        confirmLabel={t(production ? 'environment.retire.ask' : 'environment.retire.action')}
        confirmDisabled={sending || name !== env.deployment || reason.trim().length < 3}
        onConfirm={() => void retire()}
      >
        <Stack gap={12}>
          {nameField}
          <TextField id="retire-reason" label={t('environment.retire.reason')} value={reason} onChange={e => setReason(e.target.value)} />
        </Stack>
      </Dialog>

      <Dialog
        open={deciding}
        onClose={() => setDeciding(false)}
        tone="danger"
        title={t('environment.retire.decideTitle', { deployment: env.deployment })}
        description={t('environment.retire.decideBody', { reason: env.retireReason ?? '—', domain: env.domain })}
        footer={(
          <Inline gap={8} justify="end">
            <Button variant="secondary" disabled={sending} onClick={() => void decide('cancel')}>{t('environment.retire.cancel')}</Button>
            <Button variant="danger" disabled={sending} onClick={() => void decide('approve')}>{t('environment.retire.approve')}</Button>
          </Inline>
        )}
      />

      <Dialog
        open={purging}
        onClose={() => setPurging(false)}
        tone="danger"
        title={t('environment.retire.purgeTitle', { deployment: env.deployment })}
        description={t('environment.retire.purgeBody', { server: env.server })}
        confirmLabel={t('environment.retire.purgeAction')}
        confirmDisabled={sending || name !== env.deployment}
        onConfirm={() => void purge()}
      >
        <Stack gap={12}>
          {nameField}
          <Checkbox checked={reserve} onChange={(on: boolean) => setReserve(on)} label={t('environment.retire.reserve', { server: env.server })} />
        </Stack>
      </Dialog>
    </>
  );
}
